import { useState } from 'react';

import { useLocation } from 'react-router';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Typography } from 'Components/Typography';

import { moduleIngredients } from 'assets/content/landingPages/supplements';
import { ReactComponent as LogoSVG } from 'assets/images/landingPages/supplements/supplements_prose_logo.svg';
import { ReactComponent as CheckSVG } from 'assets/images/table_check.svg';
import { ReactComponent as CrossSVG } from 'assets/images/table_cross.svg';

import useResponsiveVariant from 'utils/useResponsiveVariant';

const Root = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 70.5px 0 71.5px;

  background-color: ${theme.colors.neutral[300]};
  border-bottom: 2px solid ${theme.colors.primary[400]};

  ${legacyTheme.breakpoints.up('sm')} {
    padding: 132px 0 80px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    padding: 140px 0 145px;
  }
`;

const Header = styled.div`
  padding: 0 24px 45px;

  ${legacyTheme.breakpoints.up('sm')} {
    padding: 0 72px 112px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    align-items: center;
    justify-content: space-between;

    width: 100%;
    padding: 0 135px 62px;
  }
`;

const Title = styled(Typography)`
  margin-bottom: 24px;

  font-size: 36px;
  line-height: 1.17;
  letter-spacing: normal;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 32px;
    font-size: 60px;
    letter-spacing: normal;
  }

  ${legacyTheme.breakpoints.up('md')} {
    max-width: 500px;
    margin-right: 50px;
  }
`;

const Paragraph = styled(Typography)`
  line-height: 1.5;
  letter-spacing: 0.25px;

  ${legacyTheme.breakpoints.up('md')} {
    max-width: 500px;
  }
`;

const Table = styled.table`
  width: 100%;

  &,
  & th,
  & td {
    border-collapse: collapse;
    min-height: 69px;
    border: 2px solid ${theme.colors.primary[400]};
  }

  & th:first-of-type {
    border-top: 2px solid transparent;
    border-left: 2px solid transparent;
  }

  & th,
  & td {
    height: 69px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    max-width: calc(100% - 135px * 2);
    padding: 0 135px;
  }
`;

const Check = styled(CheckSVG)`
  width: 24px;

  & g {
    stroke-width: 2px;
  }
`;

const Cross = styled(CrossSVG)`
  width: 24px;

  & g {
    stroke-width: 2px;
  }
`;

const TableHeaderBox = styled.th<{ secondcolumn?: boolean }>`
  ${({ secondcolumn }) => `
    background-color: ${secondcolumn ? theme.colors.supps[200] : 'transparent'};

    &:last-of-type {
      background-color: rgba(255, 255, 255, 0.6);
    }

    text-align: center;
    font-weight: 500;
    line-height: 1.15;

    & span {
      max-width: 90px;
      margin-left: auto;
      margin-right: auto;

      font-weight: 600;
    }

    ${legacyTheme.breakpoints.up('sm')} {
      & span {
        max-width: none;
      }
    }
  `}
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 5px;
`;

const TableBodyBox = styled.td<{ firstcolumn?: boolean; secondcolumn?: boolean }>`
  ${({ firstcolumn, secondcolumn }) => `
    min-width: ${firstcolumn ? 'none' : '100px'};
    max-width: ${firstcolumn ? '144px' : '122px'};
    padding: ${firstcolumn ? '0 8px' : '0'};

    background-color: ${secondcolumn ? theme.colors.supps[200] : 'rgba(255, 255, 255, 0.6)'};

    text-align: ${firstcolumn ? 'left' : 'center'};
    font-weight: 500;
    line-height: 1.57;
    letter-spacing: 0.5px;

    ${legacyTheme.breakpoints.up('xs')} {
      min-width: ${firstcolumn ? 'none' : '110px'};
    }

    ${legacyTheme.breakpoints.up('sm')} {
      max-width: ${firstcolumn ? '198px' : '144px'};
      padding: ${firstcolumn ? '0 24px' : '0'};
    }

    ${legacyTheme.breakpoints.up('md')} {
      min-width: ${firstcolumn ? 'none' : '193px'};
      max-width: ${firstcolumn ? '414px' : '193px'};
      padding: ${firstcolumn ? '0 48px' : '0'};
    }

    & svg {
      margin-top: 7px;
    }
  `}
`;

const Select = styled.select`
  appearance: menulist;

  max-width: 105px;
  margin-right: 2px;
  margin-left: 2px;

  border: none;
  border-bottom: 1px solid ${theme.colors.primary[400]};
  background-color: transparent;

  font-size: 14px;
  letter-spacing: 0.5px;
`;

const Text = styled(Typography)`
  letter-spacing: 0.5px;
`;

export const ModuleIngredients = () => {
  const { pathname } = useLocation();
  const { isMobile } = useResponsiveVariant();

  const [selectedIngredientName, setSelectedIngredientName] = useState<
    (typeof ingredientNamesOptions)[number] | undefined
  >(undefined);

  const isOnSearchTrafficRoute =
    pathname === '/pages/supplements-search' ||
    pathname === '/pages/supplements-before-after-search';
  const rawIngredientsTable = isOnSearchTrafficRoute
    ? moduleIngredients.ingredients.bodySearchTraffic
    : moduleIngredients.ingredients.body;

  const columnsTitles = rawIngredientsTable[0];
  const ingredientNamesOptions = columnsTitles.slice(2);

  const ingredientsTableMobile = rawIngredientsTable.map((row) => {
    const selectedIndex = selectedIngredientName
      ? columnsTitles.indexOf(selectedIngredientName)
      : 2;
    return [row[0], row[1], row[selectedIndex]];
  });

  const ingredientsTable = isMobile ? ingredientsTableMobile : rawIngredientsTable;

  return (
    <Root data-testid="supplements-landing-module">
      <Header>
        <Title markupName="p" variant="p1">
          {moduleIngredients.title}
        </Title>
        <Paragraph markupName="p" variant="p1">
          {moduleIngredients.paragraph}
        </Paragraph>
      </Header>

      <Table>
        <thead>
          <tr>
            {moduleIngredients.ingredients.header.map((el, key) => (
              <TableHeaderBox
                key={String(key)}
                colSpan={
                  key === 2 && rawIngredientsTable?.length > 1 ? rawIngredientsTable.length - 2 : 1
                }
                secondcolumn={key === 1}
              >
                {el.includes('prose') ? (
                  <Logo>
                    <LogoSVG />
                  </Logo>
                ) : (
                  <Text markupName="span" variant="mono2">
                    {el}
                  </Text>
                )}
              </TableHeaderBox>
            ))}
          </tr>
        </thead>
        <tbody>
          {ingredientsTable &&
            ingredientsTable.map((row, keyIngredient) => (
              <tr key={String(keyIngredient)}>
                {row.map((el, key) => (
                  <TableBodyBox key={String(key)} firstcolumn={key === 0} secondcolumn={key === 1}>
                    {el === true ? (
                      <Check />
                    ) : el === false ? (
                      <Cross />
                    ) : isMobile && key === 2 ? (
                      <Select
                        data-testid="supplements-ingredient-select"
                        name="ingredientNames"
                        onBlur={(event) =>
                          setSelectedIngredientName(
                            event.target.value as (typeof ingredientNamesOptions)[number],
                          )
                        }
                        onChange={(event) =>
                          setSelectedIngredientName(
                            event.target.value as (typeof ingredientNamesOptions)[number],
                          )
                        }
                        value={el}
                      >
                        {ingredientNamesOptions.map((name) => (
                          <option key={name}>{name}</option>
                        ))}
                      </Select>
                    ) : (
                      <Text markupName="span" variant="p2">
                        {el}
                      </Text>
                    )}
                  </TableBodyBox>
                ))}
              </tr>
            ))}
        </tbody>
      </Table>
    </Root>
  );
};
